import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useUserManagement } from '../context/UserManagementContext';
import { userManagementService, User } from '../services/userManagementService';

interface UserDeleteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (user: User) => Promise<{ success: boolean; message: string }>;
}

export function UserDeleteModal({ open, onClose, onConfirm }: UserDeleteModalProps) {
  const { state } = useUserManagement();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!state.userToDelete) return;

    setLoading(true);
    setError(null);

    try {
      const result = await onConfirm(state.userToDelete);
      if (result.success) {
        onClose();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) {
      return user.firstName;
    }
    if (user.lastName) {
      return user.lastName;
    }
    return user.email;
  };

  const getUserInitials = (user: User) => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }
    return user.email.charAt(0).toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!state.userToDelete) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              p: 1,
              borderRadius: '50%',
              bgcolor: 'error.light',
              color: 'error.contrastText',
            }}
          >
            <WarningIcon />
          </Box>
          <Typography variant="h6" component="div">
            Delete User
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error">{error}</Alert>
          )}

          <Alert severity="warning" icon={<WarningIcon />}>
            <Typography variant="body2">
              <strong>Warning:</strong> This action cannot be undone. The user will be permanently deleted 
              from the system along with all their associated data.
            </Typography>
          </Alert>

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              You are about to delete the following user:
            </Typography>
            
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  src={state.userToDelete.avatar || undefined}
                  sx={{
                    bgcolor: userManagementService.getRoleColor(state.userToDelete.role),
                    width: 50,
                    height: 50,
                  }}
                >
                  {getUserInitials(state.userToDelete)}
                </Avatar>
                
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {getUserDisplayName(state.userToDelete)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {state.userToDelete.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Role: {userManagementService.getRoleLabel(state.userToDelete.role)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Member since: {formatDate(state.userToDelete.createdAt)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom color="error">
              What will be deleted:
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                • User account and profile information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • All posts and comments created by this user
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • User's role and permissions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • All associated data and activity history
              </Typography>
            </Stack>
          </Box>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Alternative:</strong> Consider disabling the user instead of deleting them. 
              This will prevent them from accessing the system while preserving their data.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading}
          variant="contained"
          color="error"
          startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {loading ? 'Deleting...' : 'Delete User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
